import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Controller, useFormContext } from 'react-hook-form';
import { styled } from '@mui/material/styles';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import HelpIcon from '@mui/icons-material/Help';
import ComboLocationSelect from '@features/ComboLocationSelect';
import ContactFieldsSettings, {
    CONTACT_FIELDS,
    enabledName,
    useContactFieldsValues,
} from '@features/ContactFieldsSettings';
import { Input } from '@components/Input';
import { Select } from '@components/Select';
import { Checkbox } from '@components/Checkbox';
import { Switch } from '@components/Switch';
import { MultiSelectLabels } from '@components/MultiSelectLabels';
import { Typography } from '@components/Typography';
import Tooltip from '@components/Tooltip';
import { GatedSwitch } from '@features/Paywall/GatedSwitch';
import { ProfileType } from '@constants/integrations';
import { LabelType } from '@constants/labels';
import {
    RatingScaleType,
    ContactFormPlacement,
    SATISFACTION_THRESHOLD_DISABLED,
    isSatisfactionThresholdDisabled,
} from '@constants/surveys';
import { BILLING_TYPE_MAP } from '@constants/billing';
import { PREVIEW_LOCALES, getLocalizeLanguages } from '@constants/microsites';
import { buildInitialSurveyTexts } from '@helpers/surveyLanguages';
import { useTranslation, useLocations, useOpenUpgradeDrawer } from '@hooks';
import AiReviewSuggestions from './AiReviewSuggestions';
import CustomWording from './CustomWording';
import { CheckboxRow } from './styles';

const StyledRadioGroup = styled(RadioGroup)({
    flexDirection: 'column',
    '& .MuiFormControlLabel-root': {
        marginRight: 0,
        marginLeft: '-3px !important',
    },
});

const RadioOptions = ({ value, options, onChange, disabled }) => (
    <StyledRadioGroup value={value} onChange={e => onChange(Number(e.target.value))}>
        {options.map(option => (
            <FormControlLabel
                key={option.value}
                value={option.value}
                control={
                    <Radio
                        sx={{
                            color: 'var(--text-secondary)',
                            padding: '6px',
                            '&.Mui-checked': { color: 'var(--primary-main)' },
                        }}
                    />
                }
                label={
                    <Typography variant="body2" sx={{ marginLeft: '10px' }}>
                        {option.label}
                    </Typography>
                }
                disabled={disabled}
            />
        ))}
    </StyledRadioGroup>
);

const PLACEMENT_DESCRIPTION_KEYS = {
    [ContactFormPlacement.MAIN_SCREEN]: 'FeedbackSurveys.contactFormMainScreenDescription',
    [ContactFormPlacement.NEGATIVE_SCREEN]: 'FeedbackSurveys.contactFormNegativeScreenDescription',
};

const StyledThreshold = styled(Select)(({ value }) => ({
    width: 'fit-content',
    '& .MuiFormControl-root': {
        paddingBottom: 0,
    },
    ...(value !== 0 && {
        minWidth: '70px',
    }),
}));

const General = ({ isView, brands, selectedSurvey, availablePlatforms, account, isQrOnlyPlan = false }) => {
    const { data: labels } = useSelector(state => state.labels);

    const { t } = useTranslation();
    const { control, setValue, getValues, watch } = useFormContext();
    const { getSelectLocationsOptions } = useLocations();
    const openUpgradeDrawer = useOpenUpgradeDrawer();

    const satisfactionSurveyEnabled = watch('satisfactionSurveyEnabled');
    const ratingScaleType = watch('ratingScaleType');
    const contactFields = useContactFieldsValues();
    const isThresholdDisabled = isSatisfactionThresholdDisabled(watch('satisfactionSurveyThreshold'));
    const contactFormPlacement = watch('contactFormPlacement');
    const effectivePlacement = isThresholdDisabled ? ContactFormPlacement.MAIN_SCREEN : contactFormPlacement;
    const hasEnabledContactField =
        CONTACT_FIELDS.some(field => contactFields[enabledName(field)]) || !!watch('customFields')?.length;
    const ratingScaleOptions = [
        { value: RatingScaleType.FIVE_STAR, label: t('FeedbackSurveys.fiveStar') },
        { value: RatingScaleType.NPS_SCALE, label: t('FeedbackSurveys.npsScale') },
    ];
    const placementOptions = [
        { value: ContactFormPlacement.MAIN_SCREEN, label: t('FeedbackSurveys.contactFormMainScreen') },
        { value: ContactFormPlacement.NEGATIVE_SCREEN, label: t('FeedbackSurveys.contactFormNegativeScreen') },
    ];
    const customTextsEnabled = watch('customTextsEnabled');
    const showRatingSettings = !isQrOnlyPlan && satisfactionSurveyEnabled;
    const currentLocale = PREVIEW_LOCALES[account.locale];
    const offeredLanguages = getLocalizeLanguages(account.locale).map(option => option.value);

    const brandsOptions = useMemo(() => brands.map(item => ({ value: item.id, label: item.title })), [brands]);

    const locationsOptions = useMemo(() => getSelectLocationsOptions(), []);

    const labelsOptions = useMemo(() => labels.map(item => ({ value: item.id, label: item.name })), [labels]);

    return (
        <>
            <Controller
                control={control}
                name="name"
                render={({ field, fieldState: { error }, ref }) => (
                    <Input
                        ref={ref}
                        inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                        label={t('FeedbackSurveys.surveyName')}
                        fullWidth
                        error={!!error}
                        helperText={error ? t(error.message) : null}
                        required
                        disabled={isView}
                        sx={{ marginBottom: '8px' }}
                    />
                )}
            />
            <ComboLocationSelect
                brandsOptions={brandsOptions}
                locationsOptions={locationsOptions}
                module="surveys"
                data={selectedSurvey}
                disabled={isView}
                isView={isView}
                updatedLocationCb={value => setValue('mainLocationId', value)}
            />
            {isQrOnlyPlan ? (
                <GatedSwitch
                    gated
                    disabled={isView}
                    label={t('FeedbackSurveys.customerSatisfaction')}
                    onUpgradeClick={() =>
                        openUpgradeDrawer({
                            defaultPlan: BILLING_TYPE_MAP.PROFESSIONAL,
                            titleKey: 'Paywall.upgradeForCustomerRating',
                            subtitleKey: 'Paywall.upgradeForCustomerRatingDescription',
                        })
                    }
                />
            ) : (
                <Controller
                    control={control}
                    name="satisfactionSurveyEnabled"
                    render={({ field, ref }) => (
                        <Switch
                            {...field}
                            ref={ref}
                            checked={getValues('satisfactionSurveyEnabled')}
                            handleChange={event => {
                                setValue('satisfactionSurveyEnabled', event.target.checked, { shouldDirty: true });
                            }}
                            label={t('FeedbackSurveys.customerSatisfaction')}
                            disabled={isView}
                        />
                    )}
                />
            )}
            {showRatingSettings && (
                <>
                    <Typography variant="body2" sx={{ color: 'var(--text-primary)' }}>
                        {t('FeedbackSurveys.ratingScale')}
                    </Typography>
                    <RadioOptions
                        value={ratingScaleType}
                        options={ratingScaleOptions}
                        onChange={value => {
                            setValue('ratingScaleType', value, { shouldDirty: true });
                            setValue('satisfactionSurveyThreshold', 5, { shouldDirty: true });
                        }}
                        disabled={isView}
                    />
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                        <Typography
                            variant="body2"
                            sx={{ color: 'var(--text-primary)', marginTop: '8px', marginBottom: '16px' }}
                        >
                            {t('FeedbackSurveys.satisfactionSurveyThreshold')}
                        </Typography>
                        <Controller
                            control={control}
                            name="satisfactionSurveyThreshold"
                            render={({ field, ref }) => (
                                <StyledThreshold
                                    {...field}
                                    onChange={e => {
                                        setValue(`satisfactionSurveyThreshold`, e.target.value, {
                                            shouldDirty: true,
                                        });
                                    }}
                                    ref={ref}
                                    label={t('common.rating')}
                                    options={
                                        ratingScaleType === RatingScaleType.NPS_SCALE
                                            ? [
                                                  { label: '9', value: 5 },
                                                  { label: '7', value: 4 },
                                                  { label: '5', value: 3 },
                                                  { label: '3', value: 2 },
                                                  {
                                                      label: t('common.disabled'),
                                                      value: SATISFACTION_THRESHOLD_DISABLED,
                                                  },
                                              ]
                                            : [
                                                  { label: '5', value: 5 },
                                                  { label: '4', value: 4 },
                                                  { label: '3', value: 3 },
                                                  { label: '2', value: 2 },
                                                  {
                                                      label: t('common.disabled'),
                                                      value: SATISFACTION_THRESHOLD_DISABLED,
                                                  },
                                              ]
                                    }
                                    disabled={isView}
                                />
                            )}
                        />
                    </div>
                </>
            )}
            {(isQrOnlyPlan || showRatingSettings) && (
                <AiReviewSuggestions isView={isView} isQrOnlyPlan={isQrOnlyPlan} />
            )}
            <Typography variant="subtitle1" sx={{ color: 'var(--text-primary)', marginTop: '8px' }}>
                {t('FeedbackSurveys.customWording')}
            </Typography>
            <div style={{ display: 'flex', alignItems: 'center' }}>
                <Controller
                    control={control}
                    name="customTextsEnabled"
                    render={({ field, ref }) => (
                        <Switch
                            {...field}
                            ref={ref}
                            checked={getValues('customTextsEnabled')}
                            handleChange={event => {
                                const enabled = event.target.checked;
                                setValue('customTextsEnabled', enabled, { shouldDirty: true });
                                if (enabled && !getValues('texts')?.length) {
                                    setValue(
                                        'texts',
                                        buildInitialSurveyTexts(
                                            currentLocale,
                                            getValues('questions'),
                                            offeredLanguages,
                                        ),
                                        { shouldDirty: true },
                                    );
                                }
                            }}
                            label={t('FeedbackSurveys.useCustomWording')}
                            disabled={isView}
                        />
                    )}
                />
                <IconButton sx={{ padding: '4px !important' }}>
                    <Tooltip title={t('FeedbackSurveys.customWordingTooltip')} placement="top" enterTouchDelay={0}>
                        <HelpIcon sx={{ height: '18px !important', width: '18px !important' }} />
                    </Tooltip>
                </IconButton>
            </div>
            {customTextsEnabled && <CustomWording isView={isView} currentLocale={currentLocale} />}
            {showRatingSettings && (
                <>
                    <Typography
                        variant="subtitle1"
                        sx={{ color: 'var(--text-primary)', marginTop: '8px', marginBottom: '16px' }}
                    >
                        {t('FeedbackSurveys.contactDetails')}
                    </Typography>
                    {!isThresholdDisabled && (
                        <>
                            <Typography variant="body2" sx={{ color: 'var(--text-primary)' }}>
                                {t('FeedbackSurveys.contactFormPlacement')}
                            </Typography>
                            <RadioOptions
                                value={contactFormPlacement}
                                options={placementOptions}
                                onChange={value => setValue('contactFormPlacement', value, { shouldDirty: true })}
                                disabled={isView}
                            />
                        </>
                    )}
                    <Typography variant="body2" sx={{ color: 'var(--text-secondary)' }}>
                        {hasEnabledContactField
                            ? t(PLACEMENT_DESCRIPTION_KEYS[effectivePlacement])
                            : t('FeedbackSurveys.contactFormNoFields')}
                    </Typography>
                    <ContactFieldsSettings disabled={isView} />
                    <Typography variant="subtitle1" sx={{ color: 'var(--text-primary)', marginTop: '16px' }}>
                        {t('FeedbackSurveys.labelsTitle')}
                    </Typography>
                    <Typography
                        variant="caption"
                        sx={{ color: 'var(--text-secondary)', display: 'block', marginTop: '4px', marginBottom: '8px' }}
                    >
                        {t('FeedbackSurveys.labelsSubtitle')}
                    </Typography>
                    <Controller
                        control={control}
                        name="labels"
                        render={({ field, ref }) => (
                            <MultiSelectLabels
                                {...field}
                                ref={ref}
                                onChange={value => {
                                    setValue('labels', value, { shouldDirty: true });
                                }}
                                options={labelsOptions}
                                label={t('common.labels')}
                                fullWidth
                                disabled={isView}
                                type={LabelType.Contact}
                                hideNpsLabels
                                onCreateCallback={newLabel => {
                                    const values = getValues();
                                    setValue('labels', [...values.labels, newLabel], {
                                        shouldDirty: true,
                                    });
                                }}
                            />
                        )}
                    />
                </>
            )}
            <div style={{ paddingTop: 8 }}>
                <Typography variant="subtitle1">{t('FeedbackSurveys.reviewSites')}</Typography>
                <div style={{ paddingTop: 16 }}>
                    {availablePlatforms.map((platform, index) => (
                        <CheckboxRow key={index}>
                            <Controller
                                control={control}
                                name="reviewPlatforms"
                                render={({ field, ref }) => (
                                    <Checkbox
                                        ref={ref}
                                        disabled={isView}
                                        inputProps={{ 'aria-label': 'controlled' }}
                                        onChange={e => {
                                            const platforms = !e.target.checked
                                                ? field.value.filter(val => val !== platform.type)
                                                : [...field.value, platform.type];
                                            setValue('reviewPlatforms', platforms, { shouldDirty: true });
                                        }}
                                        checked={field.value.includes(platform.type)}
                                    />
                                )}
                            />
                            <Typography variant="body2">{`${ProfileType[platform.type]}${
                                platform.isShared ? ` (${t('SettingsBrands.shared')})` : ''
                            }`}</Typography>
                        </CheckboxRow>
                    ))}
                </div>
            </div>
        </>
    );
};

export default General;
